import { Button } from "@amzn/awsui-components-console";
import React, { useEffect, useState } from "react";

import { useNotifications } from "@/contexts/NotificationsContext";
import { useApi } from "@/hooks/useApi";

import { GetAgentTraceResponse } from "./types";

export interface AgentTraceButtonProps {
  userId: string;
  conversationId: string;
}

const AgentTraceButton: React.FC<AgentTraceButtonProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { notifyError } = useNotifications();

  const { apiGet } = useApi({ isAdmin: true });

  const triggerDownload = (url: string) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const handleClick = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...props });
      const response = await apiGet<GetAgentTraceResponse>(
        `/admin/agent-trace?${params.toString()}`
      );
      // A conversation can have multiple trace zips (one per message).
      // Download each via an anchor (not window.open, to avoid the popup
      // blocker). Stagger the clicks: browsers drop rapid back-to-back
      // programmatic downloads, keeping only the last, so space them out.
      const urls = response?.urls ?? [];
      urls.forEach((url, index) => {
        if (index === 0) {
          triggerDownload(url);
        } else {
          setTimeout(() => triggerDownload(url), index * 400);
        }
      });
    } catch (e) {
      console.error(e);
      notifyError(
        "Agent Trace Error.",
        "An error occurred while fetching Agent Trace. Please try again later."
      );
      setDisabled(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDisabled(false);
  }, [props]);

  return (
    <Button
      onClick={handleClick}
      loading={loading}
      disabled={disabled}
      disabledReason="Agent Trace is unavailable for this conversation."
    >
      Download Agent Trace
    </Button>
  );
};

export default AgentTraceButton;
